/** 全局律动采集/下发由主应用负责时的跨子应用约定 */

import {
  isKeyboardSubAppMusicRhythmEnabled,
  readCurrentKeyboardSubAppRhythmDeviceId,
  readKeyboardSubAppRhythmEquipmentFromStorage,
} from "./keyboardSubAppMusicRhythm";
import { isDesktopMusicRhythmRuntime } from "./desktopRuntime";
import { resetRhythmNativeCaptureTracking } from "./rhythmNativeCaptureTracking";
import {
  isHuafendaCrossInterfaceSamePhysicalKeyboard,
  usesSubAppV1ExclusiveVirtualKeyboard,
} from "./hfdWirelessMusicRhythm";
import { normalizeHuafendaEquipmentForSubApp } from "./hfdSdkRuntime";
import {
  buildRhythmTargetId,
  enrichEquipmentForRhythmMatch,
  equipmentMatchesRhythmTargetEquipment,
  equipmentStrictlyMatchesForRhythmIsolation,
  resolveRhythmDeviceIdForEquipment,
  rhythmTargetIdLegacyBase,
} from "./rhythmTargetId";
import { isEnteringKeyboardSubAppFromCard } from "./keyboardSubAppMusicRhythm";

/**
 * 全局音乐律动仅桌面端可用：依赖 Electron 音频采集与 HID。
 * 浏览器中会反复触发 WebHID 设备授权弹窗。
 */
export function isGlobalMusicRhythmAvailable(): boolean {
  return isDesktopMusicRhythmRuntime();
}

export const GLOBAL_MUSIC_RHYTHM_ENABLED_KEY =
  "music-management:musicRhythm:enabled";

export const GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_KEY =
  "music-management:selectedRhythmDeviceIds";

export const GLOBAL_MUSIC_RHYTHM_TARGETS_SNAPSHOT_KEY =
  "music-management:targetsSnapshot";

export type GlobalRhythmTargetSnapshotRow = {
  id: string;
  equipmentData?: Record<string, unknown>;
};

export function readGlobalRhythmTargetsSnapshot(): GlobalRhythmTargetSnapshotRow[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(
      GLOBAL_MUSIC_RHYTHM_TARGETS_SNAPSHOT_KEY
    );
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** 子应用灯光页 / 匹配：华奋达先 normalize，避免 X87 V2 有线/2.4G identifier 不一致 */
export function prepareEquipmentForGlobalRhythmMatch(
  equipment: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!equipment || typeof equipment !== "object") return null;
  let eq = enrichEquipmentForRhythmMatch(equipment);
  if (String(eq.solutionRovider ?? "") === "huafenda") {
    eq = normalizeHuafendaEquipmentForSubApp(eq);
    eq = enrichEquipmentForRhythmMatch(eq);
  }
  return eq;
}

function rhythmTargetSnapshotRowMatchesEquipment(
  equipment: Record<string, unknown>,
  row: GlobalRhythmTargetSnapshotRow
): boolean {
  if (!row.equipmentData) return false;
  const targetEq = prepareEquipmentForGlobalRhythmMatch(row.equipmentData);
  if (!targetEq) return false;
  if (equipmentMatchesRhythmTargetEquipment(equipment, targetEq)) return true;
  return isHuafendaCrossInterfaceSamePhysicalKeyboard(equipment, targetEq);
}

function findSelectedIdMatchingEquipment(
  equipment: Record<string, unknown>,
  selected: Set<string>
): string | null {
  const computed = buildRhythmTargetId(equipment);
  if (selected.has(computed)) return computed;

  const base = rhythmTargetIdLegacyBase(computed);
  for (const sid of selected) {
    if (sid === base || rhythmTargetIdLegacyBase(sid) === base) return sid;
    if (sid.startsWith(`${base}|`)) return sid;
  }

  const targets = readGlobalRhythmTargetsSnapshot();
  for (const sid of selected) {
    const row = targets.find((t) => t.id === sid);
    if (row && rhythmTargetSnapshotRowMatchesEquipment(equipment, row)) {
      return sid;
    }
    for (const candidate of targets) {
      if (!candidate.equipmentData) continue;
      if (rhythmTargetSnapshotRowMatchesEquipment(equipment, candidate)) {
        if (
          candidate.id === sid ||
          rhythmTargetIdLegacyBase(candidate.id) ===
            rhythmTargetIdLegacyBase(sid)
        ) {
          return sid;
        }
      }
    }
  }

  const resolved = resolveRhythmDeviceIdForEquipment(equipment, targets);
  if (resolved && selected.has(resolved)) return resolved;
  return null;
}

/** 从律动/子应用回设备首页后，待 SDK 就绪再恢复实体下发 */
export const RHYTHM_HOME_RESUME_PENDING_KEY =
  "music-management:rhythmHomeResumePending";

/** 子应用回主站首页时请求主应用调度 home resume（避免直接 import main-app） */
export const RHYTHM_HOME_RESUME_SCHEDULE_EVENT =
  "music-management:rhythmHomeResumeSchedule";

/** 主应用从律动内页回设备首页后恢复下发完成（子应用 Bridge 须重新 ensure 0x80） */
export const RHYTHM_HOME_RESUME_FINISHED_EVENT =
  "music-management:rhythmHomeResumeFinished";

/** 回主站首页：挡实体下发，保留勾选与采集；由 scheduleGlobalRhythmHomeResume 再开闸 */
export function markGlobalRhythmHomeResumePending(): void {
  if (!isDesktopMusicRhythmRuntime()) return;
  try {
    window.sessionStorage.setItem(RHYTHM_HOME_RESUME_PENDING_KEY, "1");
  } catch {
    // ignore
  }
  try {
    (
      window as { __keyboardDisableMusicRhythmOutput?: boolean }
    ).__keyboardDisableMusicRhythmOutput = true;
  } catch {
    // ignore
  }
}

export function requestGlobalRhythmHomeResumeSchedule(): void {
  if (!isDesktopMusicRhythmRuntime()) return;
  try {
    window.dispatchEvent(new CustomEvent(RHYTHM_HOME_RESUME_SCHEDULE_EVENT));
  } catch {
    // ignore
  }
}

export const GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_CHANGED_EVENT =
  "music-management:selectedRhythmDeviceIdsChanged";

/** 主应用 useAudioVisualizer 广播，键盘子应用在全局律动期间订阅，避免抢占 electron 回调 */
export const MUSIC_RHYTHM_AUDIO_FRAME_EVENT =
  "music-management:musicRhythm:audioFrame";

export type MusicRhythmAudioFramePayload = {
  frequencyData: number[];
  currentAmplitude: number;
  currentMode: string;
  isCapturingAudio: boolean;
  rms?: number;
  bass?: number;
  mids?: number;
  treble?: number;
  rhythmSpectrum?: number[];
  beat?: boolean;
  timestamp?: number;
};

/** 图二：至少勾选一台设备即应保持桌面端音频采集 */
export function globalRhythmWantsCapture(): boolean {
  if (!isDesktopMusicRhythmRuntime()) return false;
  return readGlobalRhythmSelectedDeviceIds().size > 0;
}

export function readGlobalRhythmSelectedDeviceIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(
      GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_KEY
    );
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

export function writeGlobalRhythmSelectedDeviceIds(ids: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_KEY,
      JSON.stringify([...ids])
    );
    window.dispatchEvent(
      new CustomEvent(GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_CHANGED_EVENT)
    );
  } catch {
    // ignore
  }
}

/** 仅当勾选列表为空时才 STOP_CAPTURE */
export function syncGlobalMusicRhythmEnabledFromSelection(
  ids: Set<string>
): void {
  const enabled = ids.size > 0;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      GLOBAL_MUSIC_RHYTHM_ENABLED_KEY,
      enabled ? "1" : "0"
    );
    window.dispatchEvent(
      new CustomEvent("music-management:musicRhythm:enabledChanged", {
        detail: { enabled },
      })
    );
    if (!enabled && isDesktopMusicRhythmRuntime()) {
      try {
        window.sessionStorage.setItem(
          "music-management:musicRhythm:captureActive",
          "0"
        );
        (window as any).electronAudioAPI?.sendAudioControl?.({
          type: "STOP_CAPTURE",
        });
        resetRhythmNativeCaptureTracking();
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }
}

/**
 * 仅取消一台设备的勾选；其它仍勾选型号继续全局律动与音频采集。
 */
export function removeDeviceFromGlobalRhythmSelection(
  deviceId: string
): Set<string> {
  const next = new Set(readGlobalRhythmSelectedDeviceIds());
  next.delete(deviceId);
  writeGlobalRhythmSelectedDeviceIds(next);
  syncGlobalMusicRhythmEnabledFromSelection(next);
  return next;
}

/**
 * 取消与当前 equipment 匹配的全局律动勾选（legacy / 快照 / 华奋达跨接口）。
 * 供 X87 V2 有线等在子应用灯光页点「是」时可靠停下发。
 */
export function removeGlobalRhythmSelectionMatchingEquipment(
  equipment: Record<string, unknown> | null | undefined
): string | null {
  if (!equipment) return null;
  const selected = readGlobalRhythmSelectedDeviceIds();
  if (selected.size === 0) return null;
  const eq = prepareEquipmentForGlobalRhythmMatch(equipment);
  if (!eq) return null;

  const direct = findSelectedIdMatchingEquipment(eq, selected);
  if (direct) {
    removeDeviceFromGlobalRhythmSelection(direct);
    return direct;
  }

  if (String(eq.solutionRovider ?? "") !== "huafenda") return null;

  const targets = readGlobalRhythmTargetsSnapshot();
  for (const sid of selected) {
    const row = targets.find((t) => t.id === sid);
    const targetEq = row?.equipmentData
      ? prepareEquipmentForGlobalRhythmMatch(row.equipmentData)
      : null;
    if (
      targetEq &&
      isHuafendaCrossInterfaceSamePhysicalKeyboard(eq, targetEq)
    ) {
      removeDeviceFromGlobalRhythmSelection(sid);
      return sid;
    }
  }
  return null;
}

/** 键盘子应用回设备连接页：session 仍有勾选 → 勿停采集、勿恢复灯效 */
export function shouldKeepGlobalRhythmAliveOnKeyboardSubAppExit(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return readGlobalRhythmSelectedDeviceIds().size > 0;
  } catch {
    return false;
  }
}

/**
 * 全局律动已开启且至少勾选一台设备：音频由主应用采集并广播。
 * 键盘子应用 musicDance 本地开关开启时不委托（两路开关独立）。
 */
/** 当前 equipment 是否在全局律动勾选列表中（含 legacy / 快照 / 华奋达有线·2.4G 跨接口） */
export function equipmentMatchesGlobalRhythmSelection(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  const selected = readGlobalRhythmSelectedDeviceIds();
  if (selected.size === 0 || !equipment) return false;
  const eq = prepareEquipmentForGlobalRhythmMatch(equipment);
  if (!eq) return false;
  return findSelectedIdMatchingEquipment(eq, selected) != null;
}

/** 解析当前设备在全局勾选列表中的 deviceId（供弹窗 / 快照 / 恢复） */
export function resolveGlobalRhythmSelectedDeviceIdForEquipment(
  equipment: Record<string, unknown> | null | undefined
): string | null {
  const selected = readGlobalRhythmSelectedDeviceIds();
  if (selected.size === 0 || !equipment) return null;
  const eq = prepareEquipmentForGlobalRhythmMatch(equipment);
  if (!eq) return null;
  return (
    findSelectedIdMatchingEquipment(eq, selected) ?? buildRhythmTargetId(eq)
  );
}

function rhythmTargetConflictsWithSubAppActiveCard(
  targetEquipment: Record<string, unknown>,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  if (
    pathname.startsWith("/musicRhythm") ||
    pathname.startsWith("/global-music-rhythm")
  ) {
    return false;
  }
  const active = readKeyboardSubAppRhythmEquipmentFromStorage();
  if (!active || typeof active !== "object") return false;
  if (
    equipmentStrictlyMatchesForRhythmIsolation(
      active as Record<string, unknown>,
      targetEquipment
    )
  ) {
    return false;
  }
  if (pathname.startsWith("/keyboard-management")) return true;
  if (isEnteringKeyboardSubAppFromCard()) return true;
  return false;
}

/**
 * 子应用内勿用其它型号覆盖 equipmentData（防串台）。
 * 不阻止对其它已勾选设备继续实体律动下发。
 */
export function shouldBlockRhythmTargetLocalStorageSync(
  targetEquipment: Record<string, unknown>,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  return rhythmTargetConflictsWithSubAppActiveCard(targetEquipment, pathname);
}

/**
 * 键盘子应用内禁止全局律动对华奋达做 setDevicesHuafenda / setMusicData(V1)。
 * 全局路径仅 connectDevice，缺少 instance.connect()，会破坏子应用灯光页 getLEDEffect；
 * 子应用华奋达实体律动由 onRetry 全量连接 + 图一 V1 桥负责。
 */
export function shouldBlockHuafendaSdkRebindWhileInSubApp(
  targetEquipment: Record<string, unknown>,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  if (String(targetEquipment.solutionRovider ?? "") !== "huafenda") {
    return false;
  }
  if (pathname.startsWith("/keyboard-management")) return true;
  if (isEnteringKeyboardSubAppFromCard()) return true;
  return false;
}

/** @deprecated 请用 shouldBlockRhythmTargetLocalStorageSync + shouldBlockHuafendaSdkRebindWhileInSubApp */
export function shouldBlockRhythmTargetRuntimeApply(
  targetEquipment: Record<string, unknown>,
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  return (
    shouldBlockRhythmTargetLocalStorageSync(targetEquipment, pathname) &&
    shouldBlockHuafendaSdkRebindWhileInSubApp(targetEquipment, pathname)
  );
}

export function isCurrentKeyboardInGlobalRhythmSelection(): boolean {
  if (!isGlobalMusicRhythmCaptureDelegatedToMain()) return false;
  const raw = readKeyboardSubAppRhythmEquipmentFromStorage();
  if (!raw || typeof raw !== "object") return false;
  const eq = raw as Record<string, unknown>;
  if (usesSubAppV1ExclusiveVirtualKeyboard(eq)) return false;
  return equipmentMatchesGlobalRhythmSelection(eq);
}

function isKeyboardSubAppPathname(
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  return pathname.startsWith("/keyboard-management");
}

/**
 * 子应用路由内：图二全局律动仍有勾选时，主应用 Bridge 须持续下发（勿因 id 对齐延迟误禁发）。
 * 仅在没有全局勾选、且未开子应用本地律动开关时，才阻断主应用 HID。
 */
export function shouldMainAppBlockRhythmHardwareOutputInKeyboardSubApp(
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  if (!isKeyboardSubAppPathname(pathname)) return false;
  if (globalRhythmWantsCapture()) return false;
  return !isKeyboardSubAppMusicRhythmEnabled(
    readCurrentKeyboardSubAppRhythmDeviceId()
  );
}

/** 同步主应用 `__keyboardDisableMusicRhythmOutput`；在子应用路由返回 true */
export function syncMainAppRhythmHardwareOutputGateInKeyboardSubApp(
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  if (!isKeyboardSubAppPathname(pathname)) return false;
  try {
    (
      window as { __keyboardDisableMusicRhythmOutput?: boolean }
    ).__keyboardDisableMusicRhythmOutput =
      shouldMainAppBlockRhythmHardwareOutputInKeyboardSubApp(pathname);
  } catch {
    // ignore
  }
  return true;
}

/** 全局律动已开且当前子应用设备在勾选列表（含子应用本地开关占用 delegation 时） */
function keyboardSubAppMatchesActiveGlobalRhythmSelection(
  eq: Record<string, unknown>
): boolean {
  if (!isDesktopMusicRhythmRuntime()) return false;
  try {
    if (window.localStorage.getItem(GLOBAL_MUSIC_RHYTHM_ENABLED_KEY) !== "1") {
      return false;
    }
  } catch {
    return false;
  }
  if (usesSubAppV1ExclusiveVirtualKeyboard(eq)) return false;
  return equipmentMatchesGlobalRhythmSelection(eq);
}

/**
 * 非 V1 独占三款时：子应用灯光页虚拟键盘可消费主应用广播的全局音频帧。
 */
export function shouldKeyboardSubAppConsumeGlobalRhythmAudioFrames(): boolean {
  if (!isDesktopMusicRhythmRuntime()) return false;
  const raw = readKeyboardSubAppRhythmEquipmentFromStorage();
  if (!raw || typeof raw !== "object") return false;
  const eq = raw as Record<string, unknown>;
  if (usesSubAppV1ExclusiveVirtualKeyboard(eq)) return false;
  if (isGlobalMusicRhythmCaptureDelegatedToMain()) {
    return equipmentMatchesGlobalRhythmSelection(eq);
  }
  return keyboardSubAppMatchesActiveGlobalRhythmSelection(eq);
}

/**
 * 全局律动期间勿调华奋达 getKeyData / getLEDEffect 等 HID 读：
 * 灯光页虚拟键盘走静态预览矩阵，实体下发由 Bridge 负责。
 */
export function shouldDeferHfdSdkLayoutReadsForGlobalRhythm(
  equipment?: Record<string, unknown> | null
): boolean {
  return shouldKeyboardSubAppEchoGlobalRhythmOnVirtualKeyboard(equipment);
}

/** 子应用灯光页是否用全局算法驱动虚拟键盘（与 V1 虚拟预览互斥） */
export function shouldKeyboardSubAppEchoGlobalRhythmOnVirtualKeyboard(
  equipment?: Record<string, unknown> | null
): boolean {
  const eq =
    equipment ??
    (readKeyboardSubAppRhythmEquipmentFromStorage() as Record<
      string,
      unknown
    > | null);
  if (!eq || typeof eq !== "object") return false;
  if (usesSubAppV1ExclusiveVirtualKeyboard(eq)) return false;
  if (isGlobalMusicRhythmCaptureDelegatedToMain()) {
    return equipmentMatchesGlobalRhythmSelection(eq);
  }
  return keyboardSubAppMatchesActiveGlobalRhythmSelection(eq);
}

export function isGlobalMusicRhythmCaptureDelegatedToMain(): boolean {
  if (typeof window === "undefined" || !isDesktopMusicRhythmRuntime()) {
    return false;
  }
  if (
    isKeyboardSubAppMusicRhythmEnabled(
      readCurrentKeyboardSubAppRhythmDeviceId()
    )
  ) {
    return false;
  }
  try {
    if (window.localStorage.getItem(GLOBAL_MUSIC_RHYTHM_ENABLED_KEY) !== "1") {
      return false;
    }
    const raw = window.sessionStorage.getItem(
      GLOBAL_MUSIC_RHYTHM_SELECTED_IDS_KEY
    );
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) && ids.length > 0;
  } catch {
    return false;
  }
}

/**
 * 全局律动音频帧广播目标。qiankun 子应用 sandbox `window` 与主应用隔离，
 * 须在同一 top/rawWindow 上 dispatch/listen，否则子应用灯光页收不到帧。
 */
export function getMusicRhythmBroadcastTargets(): Window[] {
  if (typeof window === "undefined") {
    return [];
  }
  const out: Window[] = [];
  const seen = new Set<Window>();
  const add = (candidate: Window | null | undefined) => {
    if (!candidate || seen.has(candidate)) return;
    if (typeof candidate.dispatchEvent !== "function") return;
    seen.add(candidate);
    out.push(candidate);
  };
  const w = window as Window & {
    __POWERED_BY_QIANKUN__?: boolean;
    rawWindow?: Window;
  };
  add(window);
  add(w.rawWindow);
  try {
    add(window.parent);
  } catch {
    // cross-origin
  }
  try {
    add(window.top);
  } catch {
    // cross-origin
  }
  return out.length > 0 ? out : [window];
}

/** @deprecated 使用 getMusicRhythmBroadcastTargets；保留兼容 */
export function getMusicRhythmBroadcastTarget(): Window {
  return getMusicRhythmBroadcastTargets()[0] ?? window;
}

export function dispatchMusicRhythmAudioFrame(
  data: MusicRhythmAudioFramePayload
): void {
  if (typeof window === "undefined" || !isDesktopMusicRhythmRuntime()) return;
  const event = new CustomEvent(MUSIC_RHYTHM_AUDIO_FRAME_EVENT, {
    detail: data,
  });
  try {
    for (const target of getMusicRhythmBroadcastTargets()) {
      target.dispatchEvent(event);
    }
  } catch {
    // ignore
  }
}
